<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import { isToday, isPast } from 'date-fns';
import {
  taskKey,
  getTaskDueDate,
  getTaskEndDate,
  suggestEndTime,
} from 'dashboard/helper/taskRecurrence';

const props = defineProps({
  tasks: { type: Array, default: () => [] },
  viewMode: { type: String, required: true }, // 'day' | 'week' | 'month'
});

const emit = defineEmits(['selectTask', 'createOnDate', 'rangeChange']);

const { locale } = useI18n();

const VIEW_MODE_TO_FULLCALENDAR_VIEW = {
  day: 'timeGridDay',
  week: 'timeGridWeek',
  month: 'dayGridMonth',
};

const calendarRef = ref(null);

const cssVar = name =>
  `rgb(${getComputedStyle(document.documentElement).getPropertyValue(name)})`;

const eventColor = task => {
  const dueDate = getTaskDueDate(task);
  if (task.status === 'completed') return cssVar('--slate-8');
  if (isPast(dueDate) && !isToday(dueDate)) return cssVar('--ruby-9');
  if (isToday(dueDate)) return cssVar('--teal-9');
  return cssVar('--slate-10');
};

const SNAP_MINUTES = 15;

// A drag keeps exactly the range the mirror showed, as long as it stays on one
// day (the end cannot cross midnight). Month view has no times.
const draggedEnd = ({ start, end, allDay }) =>
  !allDay && end.toDateString() === start.toDateString() ? end : null;

// Cards of 30 min or less are too short for a stacked time and title.
const isShortEvent = event =>
  !event.allDay && event.end && event.end - event.start <= 30 * 60 * 1000;

// Keeps the picked slot visible while the create dialog is open.
const DRAFT_ID = 'draft';
const draft = ref(null);

const openCreate = (start, end) => {
  emit('createOnDate', start, end);
  if (end) draft.value = { start, end };
  calendarRef.value?.getApi()?.unselect();
};

// A drag inside a single slot fires `select` and, right after, `dateClick`.
// The click is checked a moment later so the drag wins and a dragged 30 min
// slot is not replaced by the 1h click default.
const DRAG_CLICK_WINDOW_MS = 300;
let lastSelectAt = 0;

const onSelect = info => {
  lastSelectAt = Date.now();
  openCreate(info.start, draggedEnd(info));
};

const onDateClick = info => {
  const clickedAt = Date.now();
  setTimeout(() => {
    if (lastSelectAt >= clickedAt - DRAG_CLICK_WINDOW_MS) return;
    openCreate(info.date, info.allDay ? null : suggestEndTime(info.date));
  }, 50);
};

const events = computed(() =>
  props.tasks
    .map(task => ({
      id: taskKey(task),
      title: task.title,
      start: getTaskDueDate(task),
      end: getTaskEndDate(task),
      allDay: task.allDay,
      backgroundColor: eventColor(task),
      borderColor: eventColor(task),
      textColor: '#fff',
      extendedProps: { task },
    }))
    .concat(
      draft.value
        ? [
            {
              id: DRAFT_ID,
              start: draft.value.start,
              end: draft.value.end,
              backgroundColor: cssVar('--iris-9'),
              borderColor: cssVar('--iris-9'),
              classNames: ['pointer-events-none'],
            },
          ]
        : []
    )
);

const calendarOptions = computed(() => ({
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
  initialView: VIEW_MODE_TO_FULLCALENDAR_VIEW[props.viewMode],
  headerToolbar: {
    left: 'prev,next today',
    center: 'title',
    right: '',
  },
  locale: locale.value.startsWith('pt') ? ptBrLocale : 'en',
  height: '100%',
  eventDisplay: 'block',
  dayMaxEvents: 3,
  moreLinkClick: 'popover',
  slotEventOverlap: false,
  views: {
    timeGridDay: { eventMaxStack: 4 },
    timeGridWeek: { eventMaxStack: 2 },
    dayGridMonth: { eventDisplay: 'list-item' },
  },
  events: events.value,
  selectable: true,
  selectMirror: true,
  unselectAuto: true,
  eventClick: info => {
    if (info.event.id === DRAFT_ID) return;
    emit('selectTask', info.event.extendedProps.task);
  },
  snapDuration: `00:${SNAP_MINUTES}:00`,
  selectMinDistance: 5,
  select: onSelect,
  dateClick: onDateClick,
  datesSet: info => {
    emit('rangeChange', { start: info.start, end: info.end });
  },
}));

const gotoDate = date => {
  calendarRef.value?.getApi()?.gotoDate(date);
};

const clearDraft = () => {
  draft.value = null;
  calendarRef.value?.getApi()?.unselect();
};

defineExpose({ gotoDate, clearDraft });

watch(
  () => props.viewMode,
  mode => {
    calendarRef.value
      ?.getApi()
      ?.changeView(VIEW_MODE_TO_FULLCALENDAR_VIEW[mode]);
  }
);
</script>

<template>
  <div class="w-full h-full rounded-xl border border-n-weak bg-n-surface-1 p-4">
    <FullCalendar ref="calendarRef" :options="calendarOptions">
      <template #eventContent="{ event, timeText, view }">
        <div class="flex flex-col min-w-0 overflow-hidden" :title="event.title">
          <span v-if="view.type === 'dayGridMonth'" class="truncate">
            {{ timeText }} {{ event.title }}
          </span>
          <span v-else-if="isShortEvent(event)" class="truncate text-[11px]">
            {{ timeText }} {{ event.title }}
          </span>
          <template v-else>
            <span v-if="timeText" class="text-[11px] opacity-80">
              {{ timeText }}
            </span>
            <span
              class="break-words"
              :class="event.allDay ? 'truncate' : 'line-clamp-2'"
            >
              {{ event.title }}
            </span>
          </template>
        </div>
      </template>
    </FullCalendar>
  </div>
</template>
